import {
  Button,
  Input,
  Modal,
  Pagination,
  Spin,
  Tag,
  Typography,
} from '@arco-design/web-react';
import {
  IconDelete,
  IconSave,
  IconSend,
} from '@arco-design/web-react/icon';
import emptyListIllustration from 'jukun-ui-standard/dist-css/assets/states/enterprise/state-empty-list.webp';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  errorMessage,
  isAmbiguousRequestFailure,
} from '../../../common/api/httpClient';
import {
  sourceActionErrorMessage,
  withSourceSaveError,
} from '../../../common/api/sourceApplicationError';
import {
  DRAFTS_PAGE_SIZE,
  draftsApi,
} from '../api/draftsApi';
import type {
  PortalDraftDetail,
  PortalDraftListItem,
} from '../model/drafts';
import { formatShanghai } from '../model/drafts';
import {
  TenantApplicationEditPage,
  type TenantApplicationEditHandle,
} from '../../my-tasks/components/TenantApplicationEditPage';
import type { PortalApplication, PortalIdentity } from '../../../model/portalModels';
import { copyFormSessionData, requireFlowPageSynchronized, sameFormSessionToken, type FormSessionToken } from '@jukun/platform-sdk/shell';
import { formSessionError } from '@jukun/platform-sdk/core';
import { usePortalFormSessionManager, usePortalFormSessionSnapshot } from '../../../runtime/formSessionContext';
import type { PortalFormSessionManager } from '../../../runtime/formSessionManager';
import { hasLocalDraftSave, isBuiltInTenantDraft, validateDraftDetail, type DraftNavigate, type PreparedDraftSelection } from '../runtime/draftFormSelection';
import { DraftSourceFormHost, type DraftFormRuntimeState } from '../components/DraftSourceFormHost';
import { usePortalOverlay } from '../../../components/PortalOverlayProvider';
import '../styles/drafts.css';
import { useMobilePortalLayout } from '../../../mobile/app/mobilePortalEntry';
import MobileDraftsView from '../../../mobile/views/MobileDraftsView';
import { draftModel } from '../../../mobile/drafts/draftModels';

type Props = {
  initialDraftId?: string;
  onNavigate: DraftNavigate;
  preparedSelection?: PreparedDraftSelection;
  navigationLoadingMessage?: string;
  applications: PortalApplication[];
  identity: PortalIdentity;
  onSessionExpired: () => void;
};

export class DraftSavedRecoveryError extends Error {
  constructor(readonly phase: 'refresh' | 'update', readonly reason: unknown) {
    super(phase === 'refresh' ? '草稿已保存，但详情刷新失败，请重新加载后继续'
      : '草稿已保存，但表单版本更新失败，请重新加载当前草稿');
  }
}

/** One host operation covers the saved Core fact and the acknowledged SDK revision. */
export async function saveDraftSession(options: {
  manager: PortalFormSessionManager; token: FormSessionToken; detail: PortalDraftDetail; identity: PortalIdentity;
  onDetail: (detail: PortalDraftDetail) => void; prepareSubmit?: boolean; expectedOperationId?: string;
  onPending?: (operationId?: string) => void; readDetail?: typeof draftsApi.detail;
}) {
  const { manager, token, detail } = options;
  return manager.runHostOperation(token, async (snapshot, signal) => {
    const context = snapshot.context;
    if (context.contextType !== 'DRAFT' || context.draftContext.draftPublicId !== detail.summary.draftPublicId
        || context.businessObjectPublicId !== detail.summary.businessObjectPublicId) throw formSessionError('STALE');
    const value = manager.getBridge(token);
    if (value?.contextType !== 'DRAFT') throw formSessionError('NOT_READY');
    const result = await withSourceSaveError(async () => {
      let saved;
      try { saved = await (options.prepareSubmit ? value.bridge.prepareSubmit() : value.bridge.save()); }
      catch (cause) {
        const code = cause && typeof cause === 'object' ? Object.getOwnPropertyDescriptor(cause, 'code')?.value : undefined;
        if (code === 'SDK_DRAFT_SYNC_PENDING' || typeof code === 'string' && /TIMEOUT/.test(code)) {
          manager.setUncertain(token, true); options.onPending?.();
        }
        throw cause;
      }
      if (options.expectedOperationId && saved.operationId !== options.expectedOperationId) throw formSessionError('CAPABILITY_MISMATCH');
      if (saved.syncStatus === 'PENDING') {
        manager.setUncertain(token, true);
        options.onPending?.(saved.operationId);
      }
      return requireFlowPageSynchronized(saved);
    });
    if (result.syncStatus !== 'SYNCED' || result.coreDraftVersion < context.draftContext.expectedDraftVersion) throw formSessionError('UPDATE_INVALID');
    // Persisted facts are visible before the consumer acknowledges its new version.
    const savedDetail = copyFormSessionData({ ...detail, summary: { ...detail.summary, version: result.coreDraftVersion },
      flowPageContext: { ...detail.flowPageContext, draftContext: { ...detail.flowPageContext.draftContext, expectedDraftVersion: result.coreDraftVersion } } });
    options.onDetail(savedDetail);
    let current = savedDetail;
    let refreshFailure: unknown;
    try {
      const refreshed = validateDraftDetail(await (options.readDetail ?? draftsApi.detail)(detail.summary.draftPublicId, { signal }), detail.summary.draftPublicId, options.identity);
      if (refreshed.flowPageContext.businessObjectPublicId !== context.businessObjectPublicId
          || refreshed.flowPageContext.applicationCode !== context.applicationCode
          || refreshed.flowPageContext.pageRoute !== snapshot.pageRoute
          || refreshed.flowPageContext.processId !== context.processId || refreshed.flowPageContext.businessTaskId !== context.businessTaskId
          || refreshed.flowPageContext.businessObjectType !== context.businessObjectType
          || JSON.stringify(refreshed.flowPageContext.availableActions) !== JSON.stringify(context.availableActions)
          || refreshed.summary.version < result.coreDraftVersion) throw formSessionError('INPUT_INVALID');
      current = refreshed;
      options.onDetail(current);
    } catch (cause) { refreshFailure = cause; }
    try { await manager.updateDraft(token, current.summary.version); }
    catch (cause) { throw new DraftSavedRecoveryError('update', cause); }
    manager.setUncertain(token, false);
    if (refreshFailure) throw new DraftSavedRecoveryError('refresh', refreshFailure);
    return current.summary.version;
  }, { allowDraftRevisionUpdate: true });
}

export function DraftsPage(props: Props) {
  const mobile = useMobilePortalLayout();
  const { message } = usePortalOverlay();
  const manager = usePortalFormSessionManager();
  const session = usePortalFormSessionSnapshot().activeSession;
  const [items, setItems] = useState<PortalDraftListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string>();
  const mobilePage = useRef(1);
  const listPending = useRef(false);
  const [detail, setDetail] = useState<PortalDraftDetail>();
  const detailRef = useRef<PortalDraftDetail | undefined>(undefined);
  const [activeDraftId, setActiveDraftId] = useState(props.initialDraftId ?? '');
  const [token, setToken] = useState<FormSessionToken>();
  const [sourceFailure, setSourceFailure] = useState<unknown>();
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PortalDraftListItem>();
  const deletingRef = useRef(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteFailure, setDeleteFailure] = useState<{ title: string; error: string; recoveryError?: string }>();
  const tenantForm = useRef<TenantApplicationEditHandle | null>(null);
  const sourceRuntime = useRef<DraftFormRuntimeState | undefined>(undefined);
  const [sourceReadyDraftId, setSourceReadyDraftId] = useState<string>();
  const activeDraftIdRef = useRef(activeDraftId);
  activeDraftIdRef.current = activeDraftId;
  const listRequestSequence = useRef(0);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const acceptedRouteRef = useRef<string | undefined>(undefined);
  const autoSelected = useRef(!!props.initialDraftId);
  const pendingSync = useRef<{ draftId: string; operationId?: string } | undefined>(undefined);
  const submitOperation = useRef<{ draftPublicId: string; actionCode: string; operationId: string; expectedDraftVersion: number } | undefined>(undefined);
  const publishDetail = useCallback((current: PortalDraftDetail) => {
    if (current.summary.draftPublicId !== activeDraftIdRef.current) throw formSessionError('STALE');
    detailRef.current = current; setDetail(current);
    setItems(previous => previous.map(item => item.draftPublicId === current.summary.draftPublicId ? current.summary : item));
  }, []);
  const setSourceRuntime = useCallback((runtime: DraftFormRuntimeState) => {
    if (runtime.draftPublicId !== activeDraftIdRef.current) return;
    sourceRuntime.current = runtime;
    setSourceReadyDraftId(runtime.status === 'ready' ? runtime.draftPublicId : undefined);
  }, []);
  const loadList = useCallback(async (requestedPage = mobile ? 1 : page, append = false) => {
    const sequence = ++listRequestSequence.current; listPending.current = true; setLoading(true); setListError(undefined);
    try {
      const result = await draftsApi.list({ keyword: searchKeyword || undefined, page: requestedPage, pageSize: DRAFTS_PAGE_SIZE });
      if (sequence !== listRequestSequence.current) return;
      mobilePage.current = requestedPage;
      setItems(previous => append ? [...new Map([...previous, ...result.items].map(item => [item.draftPublicId, item])).values()] : result.items); setTotal(result.total);
      if (!mobile && !autoSelected.current && !props.initialDraftId && !detailRef.current && !manager.getSnapshot().latestRequest && result.items[0]) {
        autoSelected.current = true;
        void props.onNavigate(`/portal/drafts/${result.items[0].draftPublicId}`, { replace: true });
      }
    } catch (cause) { if (sequence === listRequestSequence.current) { setListError(errorMessage(cause)); message.error(errorMessage(cause)); } }
    finally { if (sequence === listRequestSequence.current) { listPending.current = false; setLoading(false); } }
  }, [mobile, page, searchKeyword, props.initialDraftId, props.onNavigate, message, manager]);
  useEffect(() => { void loadList(); return () => { ++listRequestSequence.current; }; }, [loadList]);

  useLayoutEffect(() => {
    const id = props.initialDraftId;
    const prepared = props.preparedSelection;
    if (id && prepared?.draftId !== id) {
      if (acceptedRouteRef.current === id) return;
      acceptedRouteRef.current = id; setDetailLoading(!detailRef.current);
      void props.onNavigate(`/portal/drafts/${id}`, { replace: true }).finally(() => setDetailLoading(false));
      return;
    }
    acceptedRouteRef.current = id ?? '';
    const changing = activeDraftIdRef.current !== (id ?? '')
      || sourceRuntime.current?.token?.sessionId !== prepared?.token?.sessionId;
    if (changing) { sourceRuntime.current = undefined; setSourceReadyDraftId(undefined); submitOperation.current = undefined; pendingSync.current = undefined; }
    activeDraftIdRef.current = id ?? ''; setActiveDraftId(id ?? '');
    if (!prepared || !id) { detailRef.current = undefined; setDetail(undefined); setToken(undefined); setSourceFailure(undefined); return; }
    const existing = detailRef.current;
    if (!existing || changing || existing.summary.version <= prepared.detail.summary.version) {
      detailRef.current = prepared.detail; setDetail(prepared.detail);
    }
    setToken(prepared.token); setSourceFailure(prepared.sourceFailure); setDetailLoading(false);
  }, [props.initialDraftId, props.preparedSelection, props.onNavigate]);

  const tenantDraft = !!detail && isBuiltInTenantDraft(detail);
  const matches = !!token && session?.token.sessionId === token.sessionId;
  const blocked = matches && (session.hostPending > 0 || session.state?.busy === true || session.uncertain);
  const canOperate = tenantDraft || !!matches && session?.status === 'READY' && sourceReadyDraftId === activeDraftId;
  const saveBusinessForm = async (current: PortalDraftDetail, prepareSubmit: boolean) => {
    if (isBuiltInTenantDraft(current)) {
      if (!tenantForm.current) throw new Error('草稿业务表单尚未加载');
      const version = (prepareSubmit ? await tenantForm.current.prepareSubmit() : await tenantForm.current.save())
        ?? current.flowPageContext.draftContext.expectedDraftVersion;
      publishDetail({ ...current, summary: { ...current.summary, version }, flowPageContext: { ...current.flowPageContext,
        draftContext: { ...current.flowPageContext.draftContext, expectedDraftVersion: version } } });
      try {
        const refreshed = validateDraftDetail(await draftsApi.detail(current.summary.draftPublicId), current.summary.draftPublicId, props.identity);
        if (refreshed.summary.version < version) throw formSessionError('INPUT_INVALID');
        publishDetail(refreshed); return refreshed.summary.version;
      } catch (cause) { throw new DraftSavedRecoveryError('refresh', cause); }
    }
    const runtime = sourceRuntime.current;
    if (!token || runtime?.status !== 'ready' || !runtime.token || !sameFormSessionToken(runtime.token, token)
        || runtime.draftPublicId !== current.summary.draftPublicId) throw formSessionError('NOT_READY');
    const expected = pendingSync.current?.draftId === current.summary.draftPublicId ? pendingSync.current.operationId : undefined;
    const version = await saveDraftSession({ manager, token, detail: current, identity: props.identity, prepareSubmit,
      expectedOperationId: expected, onDetail: publishDetail,
      onPending: operationId => { pendingSync.current = { draftId: current.summary.draftPublicId, operationId }; },
    });
    pendingSync.current = undefined; return version;
  };
  const saveDraft = async (showMessage: boolean) => {
    if (savingRef.current) throw formSessionError('NOT_READY');
    const active = manager.getSnapshot().activeSession;
    if (token && (active?.hostPending || active?.state?.busy
        || active?.uncertain && pendingSync.current?.draftId !== activeDraftIdRef.current)) throw formSessionError('NOT_READY');
    const current = detailRef.current;
    if (!current || current.summary.draftPublicId !== activeDraftIdRef.current || !hasLocalDraftSave(current)) throw formSessionError('NOT_READY');
    savingRef.current = true; setSaving(true);
    try { const version = await saveBusinessForm(current, false); if (showMessage) message.success('草稿已保存'); return version; }
    catch (cause) {
      if (!isBuiltInTenantDraft(current) && detailRef.current && detailRef.current.summary.version > current.summary.version) setSourceFailure(cause);
      throw cause;
    }
    finally { savingRef.current = false; setSaving(false); }
  };
  const saveRef = useRef(saveDraft); saveRef.current = saveDraft;
  useLayoutEffect(() => {
    manager.setSaveBeforeLeave(async originalToken => {
      if (!token || !sameFormSessionToken(token, originalToken)) throw formSessionError('STALE');
      await saveRef.current(false);
    });
    return () => manager.setSaveBeforeLeave(undefined);
  }, [manager, token?.sessionId]);

  const submitDraft = async (actionCode: string) => {
    if (savingRef.current) return;
    const current = detailRef.current;
    if (!current || current.summary.draftPublicId !== activeDraftIdRef.current || !canOperate
      || !current.flowPageContext.availableActions.some(action => action.actionCode === actionCode && action.operationType !== 'SHELL_LOCAL')) return;
    const active = manager.getSnapshot().activeSession;
    if (token && (active?.hostPending || active?.state?.busy || active?.uncertain
        && (submitOperation.current?.draftPublicId !== current.summary.draftPublicId || submitOperation.current?.actionCode !== actionCode))) return;
    savingRef.current = true; setSaving(true);
    let conversionStarted = false;
    const submit = async (signal?: AbortSignal) => {
      const existing = submitOperation.current;
      const retry = existing?.draftPublicId === current.summary.draftPublicId && existing.actionCode === actionCode;
      const version = retry ? existing.expectedDraftVersion : await saveBusinessForm(current, true);
      const operationId = retry ? existing.operationId : crypto.randomUUID();
      submitOperation.current = { draftPublicId: current.summary.draftPublicId, actionCode, operationId, expectedDraftVersion: version };
      conversionStarted = true;
      try {
        const result = await draftsApi.execute(current.summary.draftPublicId, { actionCode, expectedDraftVersion: version, operationId }, { signal });
        if (result.draftPublicId !== current.summary.draftPublicId) throw formSessionError('CAPABILITY_MISMATCH');
        submitOperation.current = undefined;
        if (token) { manager.setUncertain(token, false); manager.setSaveEligibility(token, false); }
        message.success(`草稿已提交，流程状态：${result.status}`);
      } catch (cause) {
        if (!isAmbiguousRequestFailure(cause)) submitOperation.current = undefined;
        else if (token) manager.setUncertain(token, true);
        throw cause;
      }
    };
    try {
      if (token && !tenantDraft) await manager.runHostOperation(token, (_snapshot, signal) => submit(signal), { allowDraftRevisionUpdate: true });
      else await submit();
    } catch (cause) {
      if (!conversionStarted && !isBuiltInTenantDraft(current) && detailRef.current && detailRef.current.summary.version > current.summary.version) setSourceFailure(cause);
      message.error(sourceActionErrorMessage(cause)); return;
    }
    finally { savingRef.current = false; setSaving(false); }
    await props.onNavigate('/portal/todos');
  };
  const deleteDraft = async (item: PortalDraftListItem) => {
    if (deletingRef.current || savingRef.current || blocked) return;
    // Serialize the leave preparation too, while keeping save-before-leave available.
    deletingRef.current = true; setDeleting(true); setDeleteFailure(undefined);
    let releaseRecoveryOwner = () => {};
    try {
      // Closing the active draft must obtain ordinary leave permission before deletion.
      if (item.draftPublicId === activeDraftId && !await props.onNavigate('/portal/drafts')) return;
      if (savingRef.current) return;
      const recoveryBase = manager.getSnapshot();
      const recoveryLocation = window.location.href;
      let recoveryRevoked = false;
      const sameSelectionOwner = () => {
        const current = manager.getSnapshot();
        return current.latestRequest === recoveryBase.latestRequest
          && current.activeSession?.token.sessionId === recoveryBase.activeSession?.token.sessionId;
      };
      // A pending selection is an accepted newer intent even before React commits.
      // Revocation is permanent if that intent subsequently fails or is cancelled.
      const unsubscribe = manager.subscribe(() => { if (!sameSelectionOwner()) recoveryRevoked = true; });
      const revokeForHistory = () => { recoveryRevoked = true; };
      window.addEventListener('popstate', revokeForHistory);
      releaseRecoveryOwner = () => { unsubscribe(); window.removeEventListener('popstate', revokeForHistory); };
      const canRecover = () => !recoveryRevoked && sameSelectionOwner() && mounted.current
        && window.location.href === recoveryLocation
        && (!activeDraftIdRef.current || activeDraftIdRef.current === item.draftPublicId);
      savingRef.current = true; setSaving(true);
      try {
        await draftsApi.delete(item.draftPublicId, item.version);
        message.success('草稿已删除'); setDeleteVisible(false); setDeleteTarget(undefined); await loadList();
      } catch (cause) {
        const failure = { title: item.title, error: errorMessage(cause) };
        // A failed mutation never grants permission to replay the old confirmation.
        setDeleteVisible(false); setDeleteTarget(undefined); setDeleteFailure(failure);
        message.error(failure.error); await loadList();
        try {
          if (!canRecover()) return;
          const refreshed = validateDraftDetail(await draftsApi.detail(item.draftPublicId), item.draftPublicId, props.identity);
          if (!canRecover()) return;
          setItems(previous => previous.map(candidate => candidate.draftPublicId === item.draftPublicId ? refreshed.summary : candidate));
          // The ordinary guarded route obtains its own fresh prepared selection/lease.
          if (!canRecover()) return;
          releaseRecoveryOwner(); releaseRecoveryOwner = () => {};
          if (!await props.onNavigate(`/portal/drafts/${item.draftPublicId}`, { replace: true })) {
            setDeleteFailure({ ...failure, recoveryError: '当前草稿详情未能恢复，请从草稿箱重新打开' });
          }
        } catch (recoveryCause) {
          if (!canRecover()) return;
          setDeleteFailure({ ...failure, recoveryError: errorMessage(recoveryCause) });
        }
      }
    } finally { releaseRecoveryOwner(); deletingRef.current = false; setDeleting(false); savingRef.current = false; setSaving(false); }
  };
  const retrySource = () => { void props.onNavigate(`/portal/drafts/${activeDraftIdRef.current}`, { force: true, replace: true, refresh: true }); };

  const actionButtons = useMemo(
    () => detail?.flowPageContext.availableActions ?? [],
    [detail],
  );

  const activeItem = items.find(
    item => item.draftPublicId === activeDraftId,
  );
  const selectedItem = detail?.summary ?? activeItem;
  const deleteFailureNotice = deleteFailure ? <div className="portal-draft-source-host__failure" role="alert">
    <strong>草稿“{deleteFailure.title}”删除未完成</strong><span>{deleteFailure.error}</span>
    {deleteFailure.recoveryError ? <span>当前草稿不可用：{deleteFailure.recoveryError}</span> : null}
  </div> : null;

  return <div className={`portal-drafts${mobile ? ' portal-drafts--mobile' : ''}${mobile && activeDraftId ? ' portal-drafts--mobile-detail' : ''}`}>
    {mobile && !activeDraftId ? deleteFailureNotice : null}
    {mobile && !activeDraftId ? <MobileDraftsView drafts={items.map(draftModel)} keyword={keyword}
      onKeywordChange={value => { setKeyword(value); if (value.trim() === searchKeyword) return; ++listRequestSequence.current; setItems([]); setTotal(0); setPage(1); mobilePage.current = 1; setSearchKeyword(value.trim()); }}
      total={total} loading={loading} error={listError} hasMore={items.length < total}
      onOpenDraft={id => { if (!savingRef.current && !deletingRef.current) void props.onNavigate(`/portal/drafts/${id}`); }}
      onRetry={() => { void loadList(); }} onLoadMore={() => { if (!listPending.current) void loadList(mobilePage.current + 1, true); }} /> : null}
    {!mobile ? <aside className="portal-approval-list">
      <div className="portal-approval-list__toolbar">
        <Input.Search
          allowClear
          placeholder="搜索草稿"
          value={keyword}
          onChange={setKeyword}
          onSearch={() => {
            setPage(1);
            setSearchKeyword(keyword.trim());
          }} />
      </div>
      <Spin loading={loading}>
        <div className="portal-approval-list__cards">
          {items.map(item => (
            <button
              className={`ui-task-card ui-task-card--oa portal-approval-card${
                item.draftPublicId === activeDraftId
                  ? ' portal-approval-card--active' : ''
              }`}
              key={item.draftPublicId}
              type="button"
              onClick={() => {
                if (saving) return;
                void props.onNavigate(
                  `/portal/drafts/${item.draftPublicId}`,
                );
              }}>
              <span className="ui-task-card__head">
                <strong className="ui-task-card__title">
                  {item.title}
                </strong>
                <Tag color="arcoblue">草稿</Tag>
              </span>
              <span className="ui-task-card__fields">
                {item.summaryItems.map(summary => (
                  <span
                    className="ui-task-card__field"
                    key={`${item.draftPublicId}-${summary.label}`}>
                    <span>{summary.label}：</span>
                    <span>{summary.value}</span>
                  </span>
                ))}
              </span>
              <span className="ui-task-card__meta">
                <span>
                  更新于 {formatShanghai(item.lastSavedAt)}
                </span>
              </span>
            </button>
          ))}
          {!loading && items.length === 0
            ? <div className="ui-state-page ui-state-page--compact portal-approval-list__empty portal-draft-empty-state">
              <img className="ui-state-page__illustration" src={emptyListIllustration} alt="" />
              <strong>暂无草稿</strong>
              <Typography.Text type="secondary">
                当前没有符合条件的草稿。
              </Typography.Text>
            </div> : null}
        </div>
      </Spin>
      <Pagination
        current={page}
        pageSize={DRAFTS_PAGE_SIZE}
        total={total}
        size="small"
        hideOnSinglePage
        onChange={setPage} />
    </aside> : null}

    {(!mobile || !!activeDraftId) ? <div className="portal-approval-detail" aria-busy={!!props.navigationLoadingMessage || detailLoading}>
      {deleteFailureNotice}
      {mobile ? <div className="portal-draft-mobile-back"><Button disabled={saving || deleting || blocked} onClick={() => { void props.onNavigate('/portal/drafts'); }}>返回草稿箱</Button></div> : null}
      {props.navigationLoadingMessage ? <div className="portal-form-navigation-overlay"
        role="status" aria-live="polite">
        <Spin loading tip={props.navigationLoadingMessage} />
      </div> : null}
      {detailLoading
        ? <div className="portal-draft-loading">
          <Spin loading tip="正在加载草稿" />
        </div>
        : detail ? <>
          <header className="ui-detail-header portal-approval-detail__header">
            <div className="ui-detail-header__main">
              <div className="ui-detail-header__content">
                <div className="ui-detail-header__title-row">
                  <strong>{detail.summary.title}</strong>
                  <Tag color="arcoblue">草稿</Tag>
                </div>
                <div className="ui-detail-header__meta portal-draft-detail__meta">
                  <span>
                    来源：{detail.summary.applicationName}
                  </span>
                  <span>
                    最后保存：
                    {formatShanghai(detail.summary.lastSavedAt)}
                  </span>
                  <span>
                    有效期至：
                    {formatShanghai(detail.summary.expiresAt)}
                  </span>
                </div>
              </div>
            </div>
          </header>
          <div className="portal-approval-detail__scroll">
            <section className="portal-approval-detail__section">
              <div className="portal-approval-detail__section-title">
                <div><span>01</span><strong>提交详情</strong></div>
                <Typography.Text type="secondary">
                  当前草稿已保存的完整内容
                </Typography.Text>
              </div>
              {tenantDraft ? <TenantApplicationEditPage
                key={detail.summary.draftPublicId}
                ref={tenantForm}
                draft={{
                  draftPublicId:
                    detail.summary.draftPublicId,
                  businessObjectPublicId:
                    detail.summary.businessObjectPublicId,
                  expectedDraftVersion:
                    detail.flowPageContext.draftContext
                      .expectedDraftVersion,
                }}
                onDraftSaved={() => undefined} />
                : <DraftSourceFormHost
                  key={detail.summary.draftPublicId}
                  detail={detail}
                  token={token} sourceFailure={sourceFailure}
                  onRuntimeChange={setSourceRuntime} onRetry={retrySource} />}
            </section>
          </div>
          <footer className="portal-task-detail-actions">
            {actionButtons.map(action => (
              <Button
                key={action.actionCode}
                icon={action.operationType === 'SHELL_LOCAL'
                  ? <IconSave /> : <IconSend />}
                type={action.emphasis === 'primary'
                  ? 'primary' : 'default'}
                status={action.emphasis === 'danger'
                  ? 'danger' : undefined}
                loading={saving}
                disabled={!canOperate || saving || deleting || (blocked && !(session?.uncertain && !session.hostPending && !session.state?.busy
                  && (action.operationType === 'SHELL_LOCAL' ? pendingSync.current?.draftId === activeDraftId
                    : submitOperation.current?.actionCode === action.actionCode)))}
                onClick={() => {
                  if (action.operationType === 'SHELL_LOCAL') {
                    void saveDraft(true).catch(cause =>
                      message.error(sourceActionErrorMessage(cause)));
                  } else {
                    void submitDraft(action.actionCode);
                  }
                }}>
                {action.label}
              </Button>
            ))}
            <Button
              status="danger"
              icon={<IconDelete />}
              disabled={!selectedItem || saving || deleting || blocked}
              onClick={() => { setDeleteTarget(selectedItem); setDeleteVisible(true); }}>
              删除
            </Button>
          </footer>
        </> : <div className="ui-state-page ui-state-page--block portal-approval-detail__empty portal-draft-empty-state">
          <img className="ui-state-page__illustration" src={emptyListIllustration} alt="" />
          <strong>暂无可查看内容</strong>
          <Typography.Text type="secondary">
            {items.length === 0 ? '当前没有可查看的草稿。' : '请选择一条草稿查看提交详情。'}
          </Typography.Text>
        </div>}
    </div> : null}
    <Modal
      title="删除草稿"
      visible={deleteVisible}
      okText="删除"
      cancelText="取消"
      okButtonProps={{ status: 'danger', loading: saving || deleting, disabled: saving || deleting || blocked }}
      onCancel={() => setDeleteVisible(false)}
      onOk={() => {
        if (deleteTarget) void deleteDraft(deleteTarget);
      }}>
      删除后无法恢复，确认删除“{deleteTarget?.title}”草稿吗？
    </Modal>
  </div>;
}
